import React, { Dispatch, SetStateAction } from 'react';

import { InjectedIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl-next';

import { ButtonItem } from '@atlaskit/menu';

import { hashCompare, short12Hash, shortHash } from 'src/utils/short-hash';

import {
  IterativeReviewSelectedDiffState,
  SelectedDiffType,
  defaultMultiCommitSelection,
  MultiCommitSelection,
} from '../tabs/diff/diff-state-context';

import { messages } from './iterative-review.i18n';
import { getCommitMessage } from './utils';

const localeFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

type CommitItemProps = {
  intl: InjectedIntl;
  commit: BB.Commit;
  index: number;
  activeIndex: number;
  setActiveIndex: Dispatch<SetStateAction<number>>;
  multiCommitSelection: MultiCommitSelection;
  setMultiCommitSelection: Dispatch<SetStateAction<MultiCommitSelection>>;
  onDiffSelected: () => void;
  updateHistory: (sourceHash: string, destinationHash?: string) => void;
  iterativeReviewSelectedDiff: IterativeReviewSelectedDiffState;
  setIterativeReviewSelectedDiff: Dispatch<
    SetStateAction<IterativeReviewSelectedDiffState>
  >;
  setInitialMultiSelectState: Dispatch<SetStateAction<MultiCommitSelection>>;
  onIRItemSelectEvent: (selectedDropdownItem: string) => void;
};

export const CommitItem = (props: CommitItemProps): JSX.Element => {
  const {
    intl,
    commit,
    index,
    activeIndex,
    setActiveIndex,
    multiCommitSelection,
    setMultiCommitSelection,
    onDiffSelected,
    updateHistory,
    iterativeReviewSelectedDiff,
    setIterativeReviewSelectedDiff,
    setInitialMultiSelectState,
    onIRItemSelectEvent,
  } = props;

  const commitMessage = getCommitMessage(commit);

  const handleOnMouseEnter = () => {
    if (
      multiCommitSelection.sourceIndex >= 0 &&
      multiCommitSelection.destinationIndex === -1
    ) {
      setActiveIndex(index);
    }
  };

  const handleOnClick = (
    event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
  ) => {
    // set single commit diff
    if (!event.shiftKey) {
      const shortSrcHash = short12Hash(commit.hash);
      const shortDestHash = commit.parents
        ? short12Hash(commit.parents[0].hash)
        : undefined;
      if (shortDestHash) {
        updateHistory(shortSrcHash, shortDestHash);
      } else {
        updateHistory(shortSrcHash);
      }
      setIterativeReviewSelectedDiff({
        sourceHash: shortSrcHash,
        destHash: shortDestHash,
        isLoading: false,
        selectedDiffType: SelectedDiffType.Single,
      });
      setMultiCommitSelection(defaultMultiCommitSelection);
      setActiveIndex(-1);
      onDiffSelected();
    } else if (
      // set source commit
      !multiCommitSelection.sourceCommit ||
      (multiCommitSelection.sourceCommit &&
        multiCommitSelection.destinationCommit)
    ) {
      if (
        multiCommitSelection.sourceCommit &&
        multiCommitSelection.destinationCommit
      ) {
        // only set initial select state if both a source
        // and dest commit have been chosen
        setInitialMultiSelectState(multiCommitSelection);
      }
      setMultiCommitSelection({
        sourceCommit: commit,
        sourceIndex: index,
        destinationCommit: undefined,
        destinationIndex: -1,
      });
      setActiveIndex(index);
    } else if (
      // set dest commit
      multiCommitSelection.sourceCommit &&
      !multiCommitSelection.destinationCommit
    ) {
      // if anchor commit is clicked again, deselect the commit
      if (index === multiCommitSelection.sourceIndex) {
        setMultiCommitSelection(defaultMultiCommitSelection);
        setActiveIndex(index);
        return;
      }

      const srcIndex = Math.min(multiCommitSelection.sourceIndex, index);
      const destIndex = Math.max(multiCommitSelection.sourceIndex, index);

      // swap src and anchor commits if selection order is reversed
      let srcCommit = multiCommitSelection.sourceCommit!;
      let destCommit = commit;
      if (index < multiCommitSelection.sourceIndex) {
        [srcCommit, destCommit] = [destCommit, srcCommit];
      }
      const shortSrcHash = short12Hash(srcCommit.hash);
      const shortDestHash = short12Hash(
        destCommit.parents ? destCommit.parents[0].hash : destCommit.hash
      );

      updateHistory(shortSrcHash, shortDestHash);
      setIterativeReviewSelectedDiff({
        sourceHash: shortSrcHash,
        destHash: shortDestHash,
        isLoading: false,
        selectedDiffType: SelectedDiffType.Custom,
      });
      setMultiCommitSelection({
        sourceCommit: srcCommit,
        sourceIndex: srcIndex,
        destinationCommit: destCommit,
        destinationIndex: destIndex,
      });
      setActiveIndex(-1);
      onDiffSelected();
    }
    onIRItemSelectEvent('custom');
  };

  const isSelected =
    // Multiple commits have been selected
    (iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.Custom &&
      index >= multiCommitSelection.sourceIndex &&
      index <= multiCommitSelection.destinationIndex) ||
    // Source commit has been selected and user is picking the other commit
    (!multiCommitSelection.destinationCommit &&
      ((index >= multiCommitSelection.sourceIndex && index <= activeIndex) ||
        (index <= multiCommitSelection.sourceIndex && index >= activeIndex))) ||
    // Single commit picked
    (iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.Single &&
      !multiCommitSelection.sourceCommit &&
      hashCompare(commit.hash, iterativeReviewSelectedDiff.sourceHash));

  return (
    <div onMouseEnter={handleOnMouseEnter} key={commit.hash}>
      <ButtonItem
        isSelected={isSelected}
        key={commit.hash}
        description={
          <FormattedMessage
            {...messages.individualCommitDescription}
            values={{
              hash: shortHash(commit.hash),
              date: intl.formatDate(commit.date, localeFormat),
            }}
          />
        }
        isDisabled={commit.parents && commit.parents.length >= 2}
        onClick={handleOnClick}
      >
        {commitMessage}
      </ButtonItem>
    </div>
  );
};
